import { AxiosError } from 'axios';

import { SecureStorage } from 'services/mmkv';

import debugLog from 'utils/debugLog.ts';
import { isWeb } from 'utils/responsiveness.ts';

import { BitmovinPlayerError } from 'components/Player/types.ts';

import SsmError from './SsmError.ts';
import {
    CONCURENCY_ERROR_CODE,
    CONCURENCY_NATIVE_ERROR_CODE,
    FAIRPLAY_RENEW_ENDPOINT,
    GENERIC_SSM_SETUP_ERROR_CODE,
    HEARTBEAT_CHECK_INTERVAL,
    INVALID_HEARTBEAT_PERIOD_ERROR_CODE,
    SSM_CONENTENT_TOKEN_ERROR_403,
    SSM_CONTENT_TOKEN_ERROR_MESSAGE,
    SSM_HEADERS,
    SSM_HEARTBEAT_ENDPOINT,
    SSM_HEARTBEAT_SEND_MESSAGE_FAILURE,
    SSM_SESSION_TOKEN_STORAGE_KEY,
    SSM_SETUP_CONCURENCY_ERROR_MESSAGE,
    SSM_SETUP_ENDPOINT,
    SSM_SETUP_FAILED_ERROR_MESSAGE,
    SSM_SETUP_INVALID_HEARTBEAT_PERIOD_ERROR_MESSAGE,
    SSM_SETUP_NO_CONTENT_TOKEN_PROVIDED_ERROR_MESSAGE,
    SSM_TEARDOWN_ENDPOINT,
    TOKEN_HEADER_KEY,
    WIDEVINE_RENEW_ENDPOINT,
} from './constants.ts';
import {
    FairplayLicenseResponse,
    HeartbeatErrorResponse,
    HeartbeatResponse,
    HeartbeatValidResponse,
    LicenseRenewResponse,
    SsmSetupResponse,
} from './types.ts';

class Ssm {
    static instance: Ssm | undefined;

    static log(...params: unknown[]): void {
        debugLog('SSM', ...params);
    }

    constructor(onError: (error: BitmovinPlayerError) => void) {
        if (Ssm.instance) {
            Ssm.instance.onErrorCallback = onError;

            return Ssm.instance;
        }

        this.onErrorCallback = onError;
        Ssm.instance = this;

        Ssm.log('initialized');
    }

    private heartbeat: number | undefined;

    private _sessionToken: string | undefined;

    public contentToken: string | undefined;

    public licenseAdded = false;

    private renewByHeartbeatTime: number | undefined;

    private renewByHeartbeatInterval: NodeJS.Timeout | number | undefined;

    private onGetContentToken: (() => Promise<string>) | undefined;

    private onErrorCallback: ((error: BitmovinPlayerError) => void) | undefined;

    public async setup(_onGetContentToken?: () => Promise<string>) {
        try {
            await this.validateSessionLeftover();

            if (!_onGetContentToken) {
                throw new Error(
                    SSM_SETUP_NO_CONTENT_TOKEN_PROVIDED_ERROR_MESSAGE,
                );
            }

            Ssm.log('setup', 'start');

            this.onGetContentToken = _onGetContentToken;
            this.contentToken = await Ssm.getContentToken(_onGetContentToken);

            const setupResponse = await fetch(SSM_SETUP_ENDPOINT, {
                method: 'POST',
                headers: {
                    ...SSM_HEADERS,
                    [TOKEN_HEADER_KEY]: this.contentToken,
                },
            });
            const json: SsmSetupResponse = await setupResponse.json();

            if (!setupResponse.ok || json.errorCode) {
                throw new SsmError(
                    json.errorCode,
                    SSM_SETUP_FAILED_ERROR_MESSAGE,
                );
            }

            Ssm.log('setup response json', json);

            this.heartbeat = json.heartbeat;

            await this.setSessionToken(json.sessionToken);

            Ssm.log(
                'setup success',
                this._sessionToken && `...${this._sessionToken.slice(-10)}`,
            );
        } catch (error: unknown) {
            Ssm.log('setup error', error);

            switch ((error as SsmError | undefined)?.code) {
                case CONCURENCY_NATIVE_ERROR_CODE:
                    throw new SsmError(
                        CONCURENCY_ERROR_CODE,
                        SSM_SETUP_CONCURENCY_ERROR_MESSAGE,
                    );
                case SSM_HEARTBEAT_SEND_MESSAGE_FAILURE:
                    throw new SsmError(
                        INVALID_HEARTBEAT_PERIOD_ERROR_CODE,
                        SSM_SETUP_INVALID_HEARTBEAT_PERIOD_ERROR_MESSAGE,
                    );
                case SSM_CONENTENT_TOKEN_ERROR_403:
                    throw error;
                default:
                    throw new SsmError(
                        GENERIC_SSM_SETUP_ERROR_CODE,
                        SSM_SETUP_FAILED_ERROR_MESSAGE,
                    );
            }
        }
    }

    public async teardown() {
        try {
            if (this._sessionToken) {
                Ssm.log(
                    'teardown start',
                    `...${this._sessionToken.slice(-10)}`,
                );

                await fetch(SSM_TEARDOWN_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        ...SSM_HEADERS,
                        [TOKEN_HEADER_KEY]: this._sessionToken,
                    },
                });

                Ssm.log(
                    'teardown success',
                    this._sessionToken && `...${this._sessionToken.slice(-10)}`,
                );

                await this.setSessionToken(undefined);
                this.cleanup();
            }
        } catch (error) {
            Ssm.log('teardown failure', error);

            this.cleanup();
        }
    }

    private async validateSessionLeftover(): Promise<void> {
        try {
            const prevSessionToken = await SecureStorage.getItem(
                SSM_SESSION_TOKEN_STORAGE_KEY,
            );

            if (prevSessionToken) {
                await this.setSessionToken(prevSessionToken);
                await this.teardown();
            }
        } catch (error) {
            Ssm.log('validateOpenSession error', error);
        }
    }

    private cleanup() {
        try {
            Ssm.log('cleanup', 'start');

            this.heartbeat = undefined;
            this.contentToken = undefined;
            this.licenseAdded = false;
            this.renewByHeartbeatTime = undefined;

            if (this.renewByHeartbeatInterval) {
                clearInterval(this.renewByHeartbeatInterval);
                this.renewByHeartbeatInterval = undefined;
            }

            Ssm.log('cleanup', 'end');
        } catch (error) {
            Ssm.log('cleanup error', error);
        }
    }

    private renewLicenseByHearbeat = async () => {
        try {
            if (
                this._sessionToken &&
                this.heartbeat &&
                this.renewByHeartbeatTime &&
                Date.now() >= this.renewByHeartbeatTime
            ) {
                Ssm.log('hearbeat', 'start');
                const heartbeatResponse = await fetch(SSM_HEARTBEAT_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        [TOKEN_HEADER_KEY]: this._sessionToken,
                    },
                });

                const heartbeatResponseJson: HeartbeatResponse =
                    await heartbeatResponse.json();

                Ssm.log(
                    'hearbeat',
                    'heartbeatResponse.ok',
                    heartbeatResponse.ok,
                );
                if (!heartbeatResponse.ok) {
                    Ssm.log('hearbeat', 'recover ssm start');
                    await this.teardown();
                    await this.setup(this.onGetContentToken);
                    Ssm.log('hearbeat', 'recover ssm end');
                } else if (
                    (
                        heartbeatResponseJson as
                            | HeartbeatValidResponse
                            | undefined
                    )?.sessionToken
                ) {
                    Ssm.log('hearbeat', 'ok');
                    await this.setSessionToken(
                        (heartbeatResponseJson as HeartbeatValidResponse)
                            .sessionToken as string,
                    );
                } else {
                    Ssm.log('hearbeat', 'error');
                    Ssm.log('hearbeat', heartbeatResponseJson);
                    const _error =
                        heartbeatResponseJson as HeartbeatErrorResponse;

                    throw new SsmError(_error.errorCode, _error.message);
                }

                this.renewByHeartbeatTime += this.heartbeat * 1000;
            }
        } catch (error) {
            Ssm.log('heartbeat error', error);

            if (this.onErrorCallback) {
                const playerError =
                    error instanceof SsmError
                        ? { code: error.code }
                        : { code: GENERIC_SSM_SETUP_ERROR_CODE };

                this.onErrorCallback(playerError);
            }
        }
    };

    public async onDrmLicenseFairplayRequest<T>(request: T) {
        if (await Ssm.isAirPlayActive()) {
            await this.teardown();
            await this.setup(this.onGetContentToken);
        }

        if (this.licenseAdded && this._sessionToken) {
            // enforced drm renewal
            return {
                ...request,
                url: FAIRPLAY_RENEW_ENDPOINT,
                headers: {
                    'content-type': 'application/octet-stream',
                    [TOKEN_HEADER_KEY]: this._sessionToken,
                },
            };
        }

        if (this.contentToken && this._sessionToken) {
            return {
                ...request,
                headers: {
                    'content-type': 'application/octet-stream',
                    [TOKEN_HEADER_KEY]: `${this.contentToken},${this._sessionToken}`,
                },
            };
        }

        return request;
    }

    public async onDrmLicenseFairplayResponse(response) {
        if (typeof response.body === 'string') {
            const decodedResponse: FairplayLicenseResponse = isWeb
                ? JSON.parse(response.body)
                : JSON.parse(atob(response.body));

            const ckcMessage = decodedResponse?.CkcMessage;
            const sessionToken = decodedResponse?.sessionToken;

            if (ckcMessage) {
                if (this.contentToken) {
                    if (sessionToken) {
                        // enforced drm
                        await this.setSessionToken(sessionToken);
                    } else if (this.heartbeat) {
                        // not enforced drm
                        this.renewByHeartbeatTime =
                            Date.now() + this.heartbeat * 1000;

                        if (this.renewByHeartbeatInterval) {
                            clearInterval(this.renewByHeartbeatInterval);
                            this.renewByHeartbeatInterval = undefined;
                        }

                        this.renewByHeartbeatInterval = setInterval(
                            this.renewLicenseByHearbeat,
                            HEARTBEAT_CHECK_INTERVAL,
                        );
                    }
                }

                return Promise.resolve({
                    ...response,
                    body: ckcMessage,
                });
            }
        }

        return Promise.resolve(response);
    }

    public onDrmLicenseWidevineRequest(request) {
        // Widevine is always drm enforced.
        if (this._sessionToken) {
            if (this.licenseAdded) {
                let body = '';

                if (isWeb) {
                    body = JSON.stringify({
                        challenge: window.btoa(
                            String.fromCharCode(
                                ...new Uint8Array(request.body),
                            ),
                        ),
                    });
                } else {
                    body = btoa(
                        JSON.stringify({
                            challenge: request.body,
                        }),
                    );
                }

                return Promise.resolve({
                    ...request,
                    url: WIDEVINE_RENEW_ENDPOINT,
                    body,
                    headers: {
                        ...request.headers,
                        [TOKEN_HEADER_KEY]: this._sessionToken,
                        'Content-Type': 'application/json',
                    },
                });
            }

            if (this.contentToken) {
                return Promise.resolve({
                    ...request,
                    headers: {
                        ...request.headers,
                        [TOKEN_HEADER_KEY]: `${this.contentToken},${this._sessionToken}`,
                    },
                });
            }
        }

        return Promise.resolve(request);
    }

    public async onDrmLicenseWidevineResponse(response) {
        if (this.licenseAdded && response.body) {
            const decodedRenewResponse: LicenseRenewResponse | undefined = isWeb
                ? JSON.parse(
                      String.fromCharCode(...new Uint8Array(response.body)),
                  )
                : JSON.parse(atob(response.body));

            if (decodedRenewResponse?.errorCode) {
                return Promise.resolve(response);
            }

            if (decodedRenewResponse?.sessionToken) {
                await this.setSessionToken(decodedRenewResponse.sessionToken);
            }

            if (decodedRenewResponse?.license) {
                if (isWeb) {
                    const str = window.atob(decodedRenewResponse.license);
                    const bufView = new Uint8Array(new ArrayBuffer(str.length));

                    for (let i = 0; i < str.length; i += 1) {
                        bufView[i] = str.charCodeAt(i);
                    }

                    return Promise.resolve({
                        ...response,
                        body: bufView,
                    });
                }

                return Promise.resolve({
                    ...response,
                    body: decodedRenewResponse.license,
                });
            }
        }

        return Promise.resolve(response);
    }

    private async setSessionToken(value: string | undefined) {
        try {
            this._sessionToken = value;

            if (value) {
                await SecureStorage.setItem(
                    SSM_SESSION_TOKEN_STORAGE_KEY,
                    value,
                );
            } else {
                await SecureStorage.removeItem(SSM_SESSION_TOKEN_STORAGE_KEY);
            }
        } catch (error) {
            Ssm.log('setSessionToken', 'error', error);
        }
    }

    private static async isAirPlayActive() {
        try {
            // TODO: Disabled due to REAC-2172 issue(s) related to DRM configuration.
            // if (Platform.OS === 'ios') {
            //     const routes =
            //         await AirplayConnectivityContext?.fetchAvAudioSessionRoutes();
            //     const airPlayRoutes = routes?.filter(
            //         (route) => route.portType === 'AirPlay',
            //     );
            //
            //     return !!airPlayRoutes?.length;
            // }

            return false;
        } catch (error) {
            Ssm.log('isAirPlayActive error', error);

            return false;
        }
    }

    /**
     * Retrieves a content token by invoking the provided asynchronous callback function.
     * If the operation fails, it logs the error and throws an appropriate error based on the context.
     *
     * @param {Function} _onGetContentToken - An asynchronous callback function that returns a Promise resolving to a string representing the content token.
     * @return {Promise<string>} A Promise that resolves to the content token string.
     * @throws {Error} Throws an error logged via the Ssm class. If the error is an Axios error with a 403 response status, throws a custom SsmError.
     */
    private static async getContentToken(
        _onGetContentToken: () => Promise<string>,
    ): Promise<string> {
        try {
            return await _onGetContentToken();
        } catch (error: any) {
            Ssm.log('getConententToken error', error);

            const maybeAxiosError = error as AxiosError;

            if (
                maybeAxiosError?.isAxiosError &&
                maybeAxiosError.response?.status === 403
            ) {
                throw new SsmError(
                    SSM_CONENTENT_TOKEN_ERROR_403,
                    SSM_CONTENT_TOKEN_ERROR_MESSAGE,
                );
            } else {
                throw error;
            }
        }
    }
}

export default Ssm;
